import { profileBookingSearchLoadingSelector } from '@/features/loading/loadingSelectors';
import {
  getProfileActiveTickets,
  getProfileHistoryTickets,
} from '@/features/profile/profileActions';
import {
  activeTicketsProfileSelector,
  historyTicketsProfileSelector,
} from '@/features/profile/profileSelectors';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';
import { TransText } from '@/i18n/trans/text';
import BookingNoItems from '@/routes/account/layout/BookingNoItems';
import TicketSummary from '@/routes/account/tickets/TicketSummary';
import { useDispatch, useSelector } from '@/store/utils';
import type { ProfileBookingTimeType } from '@/types/profile';
import { useCallback, useEffect, useMemo, type FC } from 'react';
import { toast } from 'react-toastify';

interface TicketsListProps {
  timeType: ProfileBookingTimeType;
}

const TicketsList: FC<TicketsListProps> = ({ timeType }) => {
  const dispatch = useDispatch();

  const isActive = timeType === 'active';

  const loading = useSelector(profileBookingSearchLoadingSelector);
  const bookingSearchItems = useSelector(
    isActive ? activeTicketsProfileSelector : historyTicketsProfileSelector
  );

  // Fetch data on mount if not already loaded
  useEffect(() => {
    if (!bookingSearchItems) {
      const fetchTickets = async () => {
        try {
          await dispatch(
            isActive ? getProfileActiveTickets() : getProfileHistoryTickets()
          ).unwrap();
        } catch (error) {
          toast.error(
            <TransText
              i18nKey={
                isActive
                  ? 'errorFetchingActiveTickets'
                  : 'errorFetchingHistoryTickets'
              }
            />
          );
        }
      };
      fetchTickets();
    }
  }, [dispatch, isActive, bookingSearchItems]);

  const hasMore = useMemo(() => {
    if (!bookingSearchItems) return false;
    return bookingSearchItems.data.length < bookingSearchItems.totalCount;
  }, [bookingSearchItems]);

  const handleLoadMore = useCallback(async () => {
    const offset = bookingSearchItems?.data.length ?? 0;
    try {
      await dispatch(
        isActive
          ? getProfileActiveTickets(offset)
          : getProfileHistoryTickets(offset)
      ).unwrap();
    } catch (error) {
      toast.error(
        <TransText
          i18nKey={
            isActive
              ? 'errorFetchingActiveTickets'
              : 'errorFetchingHistoryTickets'
          }
        />
      );
    }
  }, [dispatch, isActive, bookingSearchItems]);

  const loadMoreRef = useInfiniteScroll({
    hasMore,
    loading,
    onLoadMore: handleLoadMore,
  });

  if (!bookingSearchItems?.data.length) {
    return <BookingNoItems bookingType="tickets" timeType={timeType} />;
  }

  return (
    <div className="flex w-full flex-col laptop:mt-2 laptop:max-w-[690px]">
      <div className="flex w-full flex-col gap-4">
        {bookingSearchItems.data.map((item, index) => (
          <TicketSummary
            key={`${item.id}-${index}`}
            bookingSearchItem={item}
            isActive={isActive}
          />
        ))}
      </div>
      {hasMore && <div ref={loadMoreRef} />}
    </div>
  );
};

export default TicketsList;
